import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { isEmptyArray, isEmptyObject } from '@common/helpers';
import { POS_COUNTRY_CODE } from '@common/helpers/constants/my-booking/my_booking_itinerary.contant';
import { ServicingCommonStoreService } from '@common/services';
import { GetFareConditionsStoreService } from '@common/services/my-booking/get-fare-conditions/get-fare-conditions.service';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { SupportComponent } from '@lib/components/support-class';
import { MOffice } from '@lib/interfaces';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import {
  FareConditionsResponseData,
  GetOrderResponseData,
  GetOrderResponseDataOrderType,
  GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner,
} from 'src/sdk-servicing';

@Component({
  selector: 'asw-my-booking-payment',
  templateUrl: './my-booking-payment.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBookingPaymentComponent extends SupportComponent {
  @Input() functionList!: { [key: string]: Function };

  private _subscriptions: Subscription = new Subscription();
  orderData!: GetOrderResponseData;

  fareConditions!: FareConditionsResponseData;
  currencyCodeByLang: string = 'JPY';

  mOffice!: MOffice;
  constructor(
    public _change: ChangeDetectorRef,
    private _common: CommonLibService,
    private _myBookingItineraryService: MyBookingItineraryService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _fareConditionsStoreService: GetFareConditionsStoreService
  ) {
    super(_common);
  }

  reload(): void {}
  init(): void {
    this.currencyCodeByLang = this._common.aswContextStoreService.aswContextData.currencyCode;
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.orderData = servicingCommon.getOrder ?? ({} as GetOrderResponseData);
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._fareConditionsStoreService.getFareConditionsObservable().subscribe((fareCondition) => {
        this.fareConditions = fareCondition.model?.data ?? ({} as FareConditionsResponseData);
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._myBookingItineraryService.masterData$.subscribe((result) => {
        this.mOffice = result.mOffice?.[0] ?? ({} as MOffice);
        this._change.markForCheck();
      })
    );
  }
  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }

  get isHideBorderLinePaymentSummary(): boolean {
    return (
      this.showKeepMyFareTotalPrices ||
      this.showBidMyPriceFee ||
      this.showTicketTotalPrices ||
      this.showAncillaryServiceTotalPrices ||
      this.showAncillaryServiceTaxTotalPrices
    );
  }

  get setStyleObject(): Object {
    if (this.showAncillaryServiceTotalPrices) {
      return { 'border-top': 'none' };
    }
    return {};
  }

  /** [1.2.1] 支払情報サマリ */
  get showPaymentInforSummary(): boolean {
    /**
     * 1)PNR情報取得API応答.data.prices.isValid = true、表示する。
     * 2)上記以外の場合、非表示とする。
     */
    if (Boolean(this.orderData?.prices?.isValid) === true) {
      return true;
    }
    return false;
  }

  /** (1.2.4) 必要マイル: PNR情報取得API応答.data.orderType.isAwardBooking = true (特典PNR)の場合 */
  get showRequiredMileage(): boolean {
    return Boolean(this.orderData?.orderType?.isAwardBooking);
  }

  /** (1.2.5) 支払総額ラベル */
  get getPaymentTotalLabel(): string {
    return this._myBookingItineraryService.getPaymentTotalLabel(this.orderData);
  }

  /** [1.2.6] 支払総額 */
  get getTotalPrices(): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    return this._myBookingItineraryService.getTotalPrices(this.orderData) ?? [];
  }

  /** [1.2.6] 支払総額 */
  get isDifferentTotalPrices(): boolean {
    /** ※PNR情報取得API応答.data.prices.totalPrices.isAirPriceChangeRepricing=true(運賃再計算の支払総額情報の変更有)の場合、支払総額を赤色で表示する。また、読み上げ用ラベルを先頭に追加する、差分が発生している旨の文言をラベルの前に設置する。*/
    return Boolean(this.orderData?.prices?.totalPrices?.isAirPriceChangeRepricing === true);
  }

  /** [1.2.8] Keep My Fare支払総額 */
  get showKeepMyFareTotalPrices(): boolean {
    return this._myBookingItineraryService.showKeepMyFareTotalPrices(this.orderData);
  }

  /** [1.2.9] Bid My Priceアップグレード料金総額 */
  get showBidMyPriceFee(): boolean {
    return this._myBookingItineraryService.showBidMyPriceFee(this.orderData);
  }

  /** [1.2.10] 航空券金額情報 */
  get showTicketTotalPrices(): boolean {
    return this._myBookingItineraryService.showTicketTotalPrices(this.orderData);
  }

  /** [1.2.11] 運賃総額 (航空券金額情報) */
  get showFareTotalPrices(): boolean {
    return this._myBookingItineraryService.showFareTotalPrices(this.orderData);
  }

  /** [1.2.12] フライトサーチャージ (航空券金額情報) */
  get showFlightSurchargeTotalPrices(): boolean {
    return this._myBookingItineraryService.showFlightSurchargeTotalPrices(this.orderData);
  }

  /** [1.2.13] 燃油特別付加運賃等総額 (航空券金額情報) */
  get showSpecialFuelSurchargeTotalPrices(): boolean {
    return this._myBookingItineraryService.showSpecialFuelSurchargeTotalPrices(this.orderData, this.mOffice);
  }

  /** [1.2.14] 税金総額 (航空券金額情報) */
  get getTaxTotalPrices(): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    return this._myBookingItineraryService.getTaxTotalPrices(this.orderData, this.mOffice);
  }

  /** [1.2.14] 税金総額 (航空券金額情報) */
  get getLabelTaxTotalPrices() {
    return this._myBookingItineraryService.getLabelTaxTotalPrices(this.mOffice);
  }

  /** [1.2.15] 発券手数料、交換発行手数料総 (航空券金額情報) */
  get showTicketFeeTotalPrices(): boolean {
    return this._myBookingItineraryService.showTicketFeeTotalPrices(this.mOffice, this.orderData);
  }

  /** [1.2.16] Ancillaryサービス税抜総額 */
  get showAncillaryServiceTotalPrices(): boolean {
    return this._myBookingItineraryService.showAncillaryServiceTotalPrices(this.orderData);
  }

  /** [1.2.17] Ancillaryサービス税金総額 */
  get showAncillaryServiceTaxTotalPrices(): boolean {
    return this._myBookingItineraryService.showAncillaryServiceTaxTotalPrices(this.orderData);
  }

  /** Payment detail modal */
  openPaymentDetailsModal() {
    // const payload = {
    //   orderData: this.orderData,
    //   fareConditions: this.fareConditionsInput,
    // } as PaymentDetailS01P031Data;
    // this._myBookingItineraryService.clickPaymentInfoDetails(payload);
    this.functionList['clickOpenPaymentDetailsModal']();
  }

  get isAwardBooking(): boolean {
    return Boolean(this.orderData?.orderType?.isAwardBooking);
  }

  get getRequiredMileage(): number {
    return Number(this.orderData?.prices?.totalPrices?.requiredMileage);
  }

  /** [1.2.8] Keep My Fare支払総額 */
  get farePriceBase() {
    return this._myBookingItineraryService.farePriceBase(this.orderData);
  }

  /** [1.2.9] Bid My Priceアップグレード料金総額 */
  get bidMyPriceTotalPrice() {
    return this._myBookingItineraryService.bidMyPriceTotalPrice(this.orderData);
  }

  get ticketPricesTotal() {
    return this.orderData?.prices?.totalPrices?.ticketPrices?.total ?? [];
  }

  /** [1.2.11] 運賃総額 (航空券金額情報) */
  get ticketPricesBase() {
    return this._myBookingItineraryService.getServicePricesBase(this.orderData);
  }

  /** [1.2.12] フライトサーチャージ (航空券金額情報) */
  get flightSurcharges() {
    return this._myBookingItineraryService.flightSurcharges(this.orderData);
  }

  /** [1.2.13] 燃油特別付加運賃等総額 (航空券金額情報) */
  get airTransportationCharges() {
    return this._myBookingItineraryService.airTransportationCharges(this.orderData);
  }

  /** [1.2.15] 発券手数料、交換発行手数料総 (航空券金額情報) */
  get ticketPricesTotalFee() {
    return this._myBookingItineraryService.ticketPricesTotalFee(this.orderData);
  }

  /** [1.2.16] Ancillaryサービス税抜総額 */
  get servicePricesBase() {
    return this._myBookingItineraryService.servicePricesBase(this.orderData);
  }

  /** [1.2.17] Ancillaryサービス税金総額 */
  get servicePricesTotal() {
    return this._myBookingItineraryService.servicePricesTotal(this.orderData);
  }

  /** [1.2.18] Keep My Fare支払税金総額 */
  get getKeepMyFarePricesTotalTaxes(): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    return this._myBookingItineraryService.keepMyFarePricesTotalTaxes(this.orderData);
  }
}
